import {
  CreateMemberRequest,
  CreateMemberResponse,
  GetMembersResponse,
  GetOrganizationDetailsResponse,
  UpdateMemberRequest,
  UpdateMemberResponse,
  UpdateOrganizationDetailsRequest,
  UpdateOrganizationDetailsResponse,
  SoftDeleteMemberResponse,
  OrganizationAPIKeyEntity,
} from '@kemu-io/kemu-types';
import globals from '../../common/globals';
import { clerkApiInterceptor } from '../authenticatedApi/clerkApiInterceptor';

// Check the endpoint url in App.tsx
const API_NAME = globals.PLATFORM_API_NAME;

// Forces to return the entire Axios response object instead of only response.data
const returnAxiosResponse = { response: true };

/**
 * Requests the organization admin to upgrade to a paid plan
 * @returns The response from the API
 */
const requestAdminUpgrade = async (config: {
	feature: 'hub-services' | 'recipe-export' | 'assisted-ai' ;
}): Promise<{success: boolean, error?: string}> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/organization/request-upgrade?feature=${config.feature}`, { ...returnAxiosResponse });
	return response.data as {success: boolean, error?: string};
};

/**
 * Allows updating the organization details
 * @param config 
 * @returns 
 */
const updateOrganizationDetails = async (config: UpdateOrganizationDetailsRequest): Promise<UpdateOrganizationDetailsResponse> => {
	const response = await clerkApiInterceptor.patch(API_NAME, `/v1/organization/details`, { ...returnAxiosResponse, body: config });
	return response.data as UpdateOrganizationDetailsResponse;
};

/**
 * Allows getting the organization details
 * @returns 
 */
const getOrganizationDetails = async (): Promise<GetOrganizationDetailsResponse> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/organization/details`, { ...returnAxiosResponse });
	return response.data as GetOrganizationDetailsResponse;
};

/**
 * Allows adding a member to the organization
 */
const addOrganizationMember = async (config: CreateMemberRequest): Promise<CreateMemberResponse> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/organization/member`, { ...returnAxiosResponse, body: config });
	return response.data as CreateMemberResponse;
};

/**
 * Allows updating a member of the organization
 */
const updateOrganizationMember = async (id: string, config: UpdateMemberRequest): Promise<UpdateMemberResponse> => {
	const response = await clerkApiInterceptor.patch(API_NAME, `/v1/organization/member/${id}`, { ...returnAxiosResponse, body: config });
	return response.data as UpdateMemberResponse;
};

/**
 * Soft delete a member from the organization
 * @param memberId The ID of the member to delete
 * @returns Promise<SoftDeleteMemberResponse>
 */
const deleteMember = async (memberId: string): Promise<SoftDeleteMemberResponse> => {
	const response = await clerkApiInterceptor.delete(API_NAME, `/v1/organization/member/${memberId}`, { ...returnAxiosResponse });
	return response.data as SoftDeleteMemberResponse;
};

const getMembers = async (): Promise<GetMembersResponse> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/organization/members`, { ...returnAxiosResponse });
	return response.data as GetMembersResponse;
};

/**
 * Generates a new API key for the organization
 * @param comment Optional comment to identify the key's purpose
 * @returns The generated API key with metadata
 */
const generateApiKey = async (comment?: string): Promise<{ apiKey: string; id: string; createdAt: string }> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/organization/apiKeys/generate`, { ...returnAxiosResponse, body: comment ? { comment } : {} });
	return response.data as { apiKey: string; id: string; createdAt: string };
};

/**
 * Revokes an existing API key
 * @param keyId The unique identifier of the API key to revoke
 * @returns Success message
 */
const revokeApiKey = async (keyId: string): Promise<{ message: string }> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/organization/apiKeys/${keyId}/revoke`, { ...returnAxiosResponse });
	return response.data as { message: string };
};

/**
 * Deletes an API key from the organization
 * @param keyId The unique identifier of the API key to delete
 * @returns Success message
 */
const deleteApiKey = async (keyId: string): Promise<{ message: string }> => {
	const response = await clerkApiInterceptor.delete(API_NAME, `/v1/organization/apiKeys/${keyId}`, { ...returnAxiosResponse });
	return response.data as { message: string };
};

export type ListApiKeysResponse = (Omit<OrganizationAPIKeyEntity, 'key'> & {
	createdByUserName: string;
	hash: string;
})[];

/**
 * Lists all API keys for the organization
 * @returns Array of API key entities (metadata only, no plain keys)
 */
const listApiKeys = async (): Promise<ListApiKeysResponse> => {
	const response = await clerkApiInterceptor.get(API_NAME, `/v1/organization/apiKeys`, { ...returnAxiosResponse });
	return response.data as ListApiKeysResponse;
};

/**
 * Auto-generates an API key for the hub and returns a session token
 * @returns The session token to exchange with the hub for the API key
 */
const autogenApiKey = async (): Promise<{ sessionToken: string }> => {
	const response = await clerkApiInterceptor.post(API_NAME, `/v1/organization/apiKeys/autogen`, { ...returnAxiosResponse });
	return response.data as { sessionToken: string };
};

const organizationApi = {
	requestAdminUpgrade,
	updateOrganizationDetails,
	addOrganizationMember,
	updateOrganizationMember,
	deleteMember,
	getMembers,
	getOrganizationDetails,
	generateApiKey,
	revokeApiKey,
	deleteApiKey,
	listApiKeys,
	autogenApiKey,
};

export default organizationApi;

